import React, { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { subscriptionService } from '../services/subscriptionService';
import { exportService } from '../services/exportService';
import api from '../services/api';
import Layout from '../components/layout/Layout';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { getPackageName, formatDate } from '../utils/formatters';

const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const logoInputRef = useRef(null);

  const [thresholdAmount, setThresholdAmount] = useState(user?.settings?.thresholdAmount || 10);
  const [savingThreshold, setSavingThreshold] = useState(false);

  const [notifications, setNotifications] = useState({
    emailAlerts: user?.settings?.notifications?.emailAlerts ?? true,
    smsAlerts: user?.settings?.notifications?.smsAlerts ?? false,
    whatsappAlerts: user?.settings?.notifications?.whatsappAlerts ?? false,
    thresholdAlerts: user?.settings?.notifications?.thresholdAlerts ?? true,
    debtAlerts: user?.settings?.notifications?.debtAlerts ?? false,
    paymentReminders: user?.settings?.notifications?.paymentReminders ?? false
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);

  // Business Profile State
  const [businessName, setBusinessName] = useState(user?.businessName || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [logoPreview, setLogoPreview] = useState(user?.logo?.url ? `${process.env.REACT_APP_API_URL}${user.logo.url}` : null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  const handleSaveThreshold = async () => {
    setSavingThreshold(true);
    try {
      const response = await api.put('/merchants/me/settings', {
        thresholdAmount: parseFloat(thresholdAmount)
      });
      updateUser({ settings: response.data.settings });
      showSuccess('Threshold updated successfully!');
    } catch (error) {
      showError(error.message || 'Failed to update threshold');
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      const response = await api.put('/merchants/me/settings', {
        notifications
      });
      updateUser({ settings: response.data.settings });
      showSuccess('Notification preferences updated successfully!');
    } catch (error) {
      showError(error.message || 'Failed to update notification preferences');
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      await exportService.exportDebtorsExcel();
      showSuccess('Excel file downloaded successfully!');
    } catch (error) {
      showError(error.message || 'Failed to export to Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportCSV = async () => {
    setExportingCSV(true);
    try {
      await exportService.exportDebtorsCSV();
      showSuccess('CSV file downloaded successfully!');
    } catch (error) {
      showError(error.message || 'Failed to export to CSV');
    } finally {
      setExportingCSV(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showError('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.put('/merchants/me/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      showSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showError(error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      await subscriptionService.exportPDF();
      showSuccess('PDF report has been sent to your email!');
    } catch (error) {
      showError(error.message || 'Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Only image files are allowed (jpeg, jpg, png, gif, webp)');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      showError('File size must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/merchants/me/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const logoUrl = `${process.env.REACT_APP_API_URL}${response.data.logo.url}`;
      setLogoPreview(logoUrl);
      updateUser({ logo: response.data.logo });
      showSuccess('Logo uploaded successfully!');
    } catch (error) {
      showError(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete your company logo?')) {
      return;
    }

    setUploadingLogo(true);
    try {
      await api.delete('/merchants/me/logo');
      setLogoPreview(null);
      updateUser({ logo: null });
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      showSuccess('Logo deleted successfully!');
    } catch (error) {
      showError(error.message || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!businessName.trim()) {
      showError('Business name is required');
      return;
    }

    if (!mobile.trim()) {
      showError('Mobile number is required');
      return;
    }

    setSavingProfile(true);
    try {
      const response = await api.put('/merchants/me', {
        businessName: businessName.trim(),
        mobile: mobile.trim()
      });

      updateUser({
        businessName: response.data.businessName,
        mobile: response.data.mobile
      });
      showSuccess('Business profile updated successfully!');
    } catch (error) {
      showError(error.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <Layout title="Settings">
      <div className="max-w-4xl mx-auto">
        {/* Business Profile */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Profile</h2>

          <div className="space-y-6">
            {/* Logo Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="relative">
                    <img
                      src={logoPreview}
                      alt="Company Logo"
                      className="w-24 h-24 object-contain rounded-lg border border-gray-300"
                    />
                    <button
                      onClick={handleDeleteLogo}
                      disabled={uploadingLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 disabled:opacity-50"
                      title="Delete logo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label
                    htmlFor="logo-upload"
                    className={`inline-block px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700 ${uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {uploadingLogo ? 'Uploading...' : logoPreview ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Maximum file size: 2MB. Accepted formats: JPEG, PNG, GIF, WebP
                  </p>
                </div>
              </div>
            </div>

            {/* Business Name & Mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Business Name"
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Enter your business name"
                required
              />
              <Input
                label="Mobile Number"
                type="text"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                placeholder="Enter your mobile number"
                required
              />
            </div>

            <Button
              onClick={handleSaveProfile}
              loading={savingProfile}
            >
              Save Business Profile
            </Button>
          </div>
        </div>

        {/* Subscription Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Current Package</p>
              <p className="text-lg font-medium text-gray-900">{getPackageName(user?.subscription?.package)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-medium capitalize text-green-600">{user?.subscription?.status}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Expiry Date</p>
              <p className="text-lg font-medium text-gray-900">
                {user?.subscription?.expiryDate ? formatDate(user.subscription.expiryDate) : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Days Remaining</p>
              <p className="text-lg font-medium text-gray-900">
                {user?.subscription?.daysRemaining || 0} days
              </p>
            </div>
          </div>

          {/* Renewal Button */}
          <div className="border-t border-gray-200 pt-6">
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-blue-800">
                    Subscription Renewal
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>Renew your subscription for 5 BHD/month to continue accessing all features.</p>
                  </div>
                </div>
              </div>
            </div>
            <a
              href="https://portal.myfatoorah.com/BHR/le/0505142593478152"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="mr-2 -ml-1 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Renew Subscription (5 BHD)
            </a>
          </div>
        </div>

        {/* Threshold Settings */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Alert Threshold</h2>
          <p className="text-sm text-gray-600 mb-4">
            Set the amount at which you'll be alerted when a debtor's outstanding balance exceeds this limit.
          </p>

          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Input
                label="Threshold Amount (BHD)"
                type="number"
                step="0.001"
                value={thresholdAmount}
                onChange={(e) => setThresholdAmount(e.target.value)}
              />
            </div>
            <Button
              onClick={handleSaveThreshold}
              loading={savingThreshold}
            >
              Save Threshold
            </Button>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Notification Preferences</h2>
          <p className="text-sm text-gray-600 mb-4">
            Configure how you want to be notified about debtor activities.
          </p>

          <div className="space-y-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.emailAlerts}
                onChange={(e) => setNotifications({ ...notifications, emailAlerts: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">Email Alerts</div>
                <div className="text-sm text-gray-500">Receive notifications via email</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.smsAlerts}
                onChange={(e) => setNotifications({ ...notifications, smsAlerts: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">SMS Alerts</div>
                <div className="text-sm text-gray-500">Receive notifications via SMS (charges apply)</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.whatsappAlerts}
                onChange={(e) => setNotifications({ ...notifications, whatsappAlerts: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">WhatsApp Alerts</div>
                <div className="text-sm text-gray-500">Receive notifications via WhatsApp (charges apply)</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.thresholdAlerts}
                onChange={(e) => setNotifications({ ...notifications, thresholdAlerts: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">Threshold Alerts</div>
                <div className="text-sm text-gray-500">Alert when debtor exceeds threshold amount</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.debtAlerts}
                onChange={(e) => setNotifications({ ...notifications, debtAlerts: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">Debt Alerts</div>
                <div className="text-sm text-gray-500">Notify when new debt is added to debtor</div>
              </div>
            </label>

            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={notifications.paymentReminders}
                onChange={(e) => setNotifications({ ...notifications, paymentReminders: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <div>
                <div className="font-medium text-gray-900">Payment Reminders</div>
                <div className="text-sm text-gray-500">Automatically send payment reminders to debtors</div>
              </div>
            </label>
          </div>

          <div className="mt-4">
            <Button
              onClick={handleSaveNotifications}
              loading={savingNotifications}
            >
              Save Notification Preferences
            </Button>
          </div>
        </div>

        {/* Export Data */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Export Data</h2>
          <p className="text-sm text-gray-600 mb-4">
            Export your debtor data in various formats.
          </p>

          <div className="flex gap-3 mb-6">
            <Button
              onClick={handleExportExcel}
              loading={exportingExcel}
              variant="outline"
            >
              Export to Excel
            </Button>
            <Button
              onClick={handleExportCSV}
              loading={exportingCSV}
              variant="outline"
            >
              Export to CSV
            </Button>
          </div>

          <h3 className="text-md font-semibold text-gray-900 mb-2">PDF Report</h3>

          <p className="text-sm text-gray-600 mb-4">
            Generate a PDF report of all your debtors and transaction history. The report will be sent to your email.
          </p>
          <Button
            onClick={handleExportPDF}
            loading={exportingPDF}
            variant="outline"
          >
            Export to PDF
          </Button>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h2>

          <form onSubmit={handlePasswordChange}>
            <Input
              label="Current Password"
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              required
            />

            <Input
              label="New Password"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              placeholder="Min. 8 characters"
              required
            />

            <Input
              label="Confirm New Password"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              required
            />

            <Button
              type="submit"
              loading={changingPassword}
            >
              Change Password
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
